import axios from 'axios';
import { apiClient } from '../../lib/api';
import { endpointRateLimitBackoff } from './endpointRateLimitBackoff';
import {
  SPORTSNEW_AUTH_SESSION_STALE_CODE,
  isSportsnewAccountCarrierCurrent,
  sportsnewLocalRequestRejection,
} from './sportsnewAccountCarrier';

const ACCOUNT_PATHS = Object.freeze({
  '/member/wallet-summary': 'GET',
  '/sports/bets': 'POST',
  '/sports-inplay/bets': 'POST',
});
const CONFIG_FIELDS = new Set(['method', 'data', 'headers', 'signal']);
const CHANGED_REQUEST = 'SPORTS_ACCOUNT_REQUEST_CHANGED';

const uncertainResponse = (staleSession, code) => ({
  status: 0,
  headers: {},
  data: { status: false, networkError: true, ...(staleSession ? { staleSession: true } : {}), code },
});

export const createSportsAccountTransport = () => {
  // Read only the effective native settings. The service initializer's 50s
  // timeout is ignored by Crypto's shared wrapper; the actual default is 20s.
  // Do not copy mutable auth defaults or either global interceptor stack.
  const nativeHeader = name => apiClient.defaults.headers[name]
    || apiClient.defaults.headers.common?.[name];
  const native = Object.freeze({
    baseURL: apiClient.defaults.baseURL,
    timeout: apiClient.defaults.timeout,
    withCredentials: apiClient.defaults.withCredentials,
    contentType: nativeHeader('Content-Type') || 'application/json',
    language: nativeHeader('custom-language') || 'ko',
  });
  const client = axios.create({
    baseURL: native.baseURL,
    timeout: native.timeout,
    withCredentials: native.withCredentials,
    headers: { 'Content-Type': native.contentType, 'custom-language': native.language },
  });

  const request = async (url, config = {}, carrier) => {
    if (!isSportsnewAccountCarrierCurrent(carrier)) throw sportsnewLocalRequestRejection();
    const method = ACCOUNT_PATHS[url];
    if (!Object.prototype.hasOwnProperty.call(ACCOUNT_PATHS, url) || String(config.method).toUpperCase() !== method
      || Object.keys(config).some(key => !CONFIG_FIELDS.has(key))) {
      throw sportsnewLocalRequestRejection(CHANGED_REQUEST);
    }
    const requestHeaders = axios.AxiosHeaders.from(config.headers);
    let body;
    try { body = config.data === undefined ? undefined : JSON.stringify(config.data); }
    catch { throw sportsnewLocalRequestRejection(CHANGED_REQUEST); }
    if (method === 'GET' && body !== undefined) throw sportsnewLocalRequestRejection(CHANGED_REQUEST);
    const language = requestHeaders.get('custom-language') || native.language;
    const authorization = `Bearer ${carrier.token}`;
    const rateLimitConfig = { method, url };
    endpointRateLimitBackoff.assertRequestAllowed(rateLimitConfig);
    const wireAdapter = axios.getAdapter(client.defaults.adapter);
    let dispatched = false;
    try {
      const response = await client(url, {
        method,
        baseURL: native.baseURL,
        timeout: native.timeout,
        withCredentials: native.withCredentials,
        data: body,
        signal: config.signal,
        headers: {
          'Content-Type': native.contentType,
          'custom-language': language,
          Authorization: authorization,
        },
        adapter: finalConfig => {
          // Axios has now merged defaults, transformed the body and completed
          // async interceptors. Freeze the command and account at the wire.
          if (!isSportsnewAccountCarrierCurrent(carrier)) throw sportsnewLocalRequestRejection();
          const finalHeaders = axios.AxiosHeaders.from(finalConfig.headers);
          if (finalConfig.url !== url || finalConfig.baseURL !== native.baseURL
            || String(finalConfig.method).toUpperCase() !== method
            || finalConfig.data !== body || finalConfig.timeout !== native.timeout
            || finalConfig.withCredentials !== native.withCredentials
            || finalConfig.auth != null || finalConfig.params != null
            || finalHeaders.get('Authorization') !== authorization
            || finalHeaders.get('Content-Type') !== native.contentType
            || finalHeaders.get('custom-language') !== language) {
            throw sportsnewLocalRequestRejection(CHANGED_REQUEST);
          }
          // Another request may have received a 429 while an interceptor was
          // waiting. Share the same native endpoint cooldown immediately here.
          endpointRateLimitBackoff.assertRequestAllowed(rateLimitConfig);
          dispatched = true;
          return wireAdapter(finalConfig);
        },
      });
      endpointRateLimitBackoff.resetAfterSuccess(rateLimitConfig);
      // Preserve the native response for the original request. Callers fence
      // wallet, pending state and success modal effects after awaiting it.
      return response;
    } catch (error) {
      if (dispatched && error?.response?.status === 429
        && !endpointRateLimitBackoff.isSyntheticRateLimitError(error)) {
        endpointRateLimitBackoff.registerServerRateLimit(rateLimitConfig, error.response.headers);
      }
      if (!dispatched) {
        if (error?.response?.data?.localRequestRejected
          || endpointRateLimitBackoff.isSyntheticRateLimitError(error)) throw error;
        throw sportsnewLocalRequestRejection(axios.isCancel(error) ? 'SPORTSNEW_REQUEST_ABORTED' : CHANGED_REQUEST);
      }
      if (!isSportsnewAccountCarrierCurrent(carrier)) {
        return uncertainResponse(true, SPORTSNEW_AUTH_SESSION_STALE_CODE);
      }
      // Keep HTTP data/status for receipt policy. Crypto sports has no native
      // logout/403 redirect policy. Network uncertainty never proves rejection.
      if (error?.response) throw error;
      return uncertainResponse(false, error?.code || 'SPORTSNEW_NETWORK_ERROR');
    }
  };
  return { client, request };
};
